import produce from "immer";
import {
  compact,
  constant,
  each,
  flatMap,
  get,
  map,
  merge,
  range,
  set,
  some,
  times,
  toInteger,
  uniq,
  without,
} from "lodash";
import { PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Timezone } from "@linkfluence/models/timezone";
import { Card } from "@radarly/models/cards";
import { hardMergeClauses } from "@radarly/models/clause/merge";
import { setCardTimezone } from "../cards/editor";
import { isCardWithRequest } from "../cards/is";
import { Clause } from "../clause";
import { Project } from "../project";
import { SearchFragment } from "../search";
import { Workspace } from "../workspace";
import {
  CardRow,
  InsightPage,
  InsightPageAccess,
  Layout,
  Row,
  TitleRow,
} from "./model";
import { getNewCardRow, getNewTitleRow, isCardRow, isTitleRow } from "./row";

export type PasteID = "paste_filters" | "paste_card";

export type PastePayload = Card | Clause;

export type PastedData = {
  cards: Card[];
  filters: { cardTitle: string; clause: Clause }[];
};

export type PastedFilter = {
  cardTitle: string;
  period: PeriodRange;
  clauses: { field: string; clause: Clause }[];
};

export type PastedCard = Card & {
  color: string;
  vizOptions: { [key: string]: boolean };
};

export const getCopiedDataKey = (
  projectId: Project["id"],
  dashboardId?: Workspace["id"]
) =>
  "pageCopiedData_" +
  (!!dashboardId ? "dash_" + dashboardId : "project_" + projectId);

export function updateTitle(page: InsightPage, title: string) {
  return produce(page, (draftPage) => {
    draftPage.title = title;
  });
}

export function updateAccess(
  page: InsightPage,
  access: InsightPageAccess,
  sharedWith: string[]
) {
  return produce(page, (draftPage) => {
    draftPage.access = access;
    draftPage.sharedWith = sharedWith;
  });
}

export function updateSettings(
  page: InsightPage,
  isFilterDynamicEnabled: boolean,
  isPeriodSelectorEnabled: boolean,
  filterShortcuts?: Facet[]
) {
  return produce(page, (draftPage) => {
    draftPage.filters.dynamic = isFilterDynamicEnabled;
    draftPage.periodSelector = isPeriodSelectorEnabled;
    draftPage.filters.static = filterShortcuts;
  });
}

export function addTitleRow(page: InsightPage, position: number) {
  return produce(page, (draftPage) => {
    draftPage.rows.splice(position, 0, getNewTitleRow());
  });
}

export function addCardRow(page: InsightPage, position: number) {
  return produce(page, (draftPage) => {
    draftPage.rows.splice(position, 0, getNewCardRow());
  });
}

export function setCardTitle(
  page: InsightPage,
  row: number,
  position: number,
  title: string
) {
  return produce(page, (draftPage) => {
    if (draftPage.rows[row]) {
      draftPage.rows[row] = setRowLabel(draftPage.rows[row], position, title);
    }
  });
}

export function setCardFilters(
  page: InsightPage,
  row: number,
  position: number,
  clause: Clause
) {
  return produce(page, (draftPage) => {
    const target = draftPage.rows[row];
    if (isCardRow(target)) {
      const card = target.cards[position];
      if (card.fragments.length && isCardWithRequest(card)) {
        card.fragments[0].request.query = clause;
        // Previous period is not part of the copied filters
        if (card.fragments[1]) {
          card.fragments.splice(1, 1); // Remove fragments[1]
        }
      }
    }
  });
}

export function setInsightPageDescription(
  page: InsightPage,
  description: string
) {
  return produce(page, (draftPage) => {
    draftPage.description = description;
  });
}

export function setInsightPageAIContext(page: InsightPage, context: string) {
  return produce(page, (draftPage) => {
    draftPage.aiContext = context;
  });
}

export function pasteFiltersOnCard(card: Card, clause: Clause) {
  if (!isCardWithRequest(card)) {
    return card;
  }
  return produce(card, (draftCard) => {
    draftCard.fragments[0].request.query = clause;
    // Previous period is not part of the copied filters
    if (draftCard.fragments[1]) {
      card.fragments.splice(1, 1); // Remove fragments[1]
    }
  });
}

const isCardFragment = (card: Card): boolean => {
  if (get(card, "fragments")) return card.fragments.length > 0;
  return false;
};

export function setGlobalCardFilters(
  page: InsightPage,
  clause: Clause
): InsightPage {
  return produce(page, (draftPage) => {
    return draftPage.rows.forEach((row) => {
      if (isCardRow(row)) {
        row.cards.forEach((card) => {
          if (isCardFragment(card) && isCardWithRequest(card)) {
            card.fragments.forEach((fragment: SearchFragment) => {
              const currentClause = fragment.request.query;
              const newClause = hardMergeClauses(clause, currentClause);
              fragment.request.query = newClause;
            });
          }
          return card;
        });
      }
    });
  });
}

function setRowLabel(row: Row, position: number, title: string) {
  if (isTitleRow(row)) {
    return setTitleRowLabel(row, position, title);
  } else if (isCardRow(row)) {
    return setCardRowLabel(row, position, title);
  }
}

function setCardRowLabel(row: CardRow, position: number, title: string) {
  return produce(row, (draft) => {
    if (draft.cards[position]) {
      draft.cards[position].title = title;
    }
  });
}

function setTitleRowLabel(row: TitleRow, position: number, title: string) {
  return produce(row, (draft) => {
    draft.titles[position] = title;
  });
}

export function cloneRow(page: InsightPage, position: number) {
  return produce(page, (draftPage) => {
    draftPage.rows.splice(position + 1, 0, draftPage.rows[position]);
  });
}

export function deleteRow(page: InsightPage, position: number) {
  return produce(page, (draftPage) => {
    draftPage.rows.splice(position, 1);
  });
}

const getLayoutItemNb = (layout: Layout) => {
  return layout.split("_").length === 3 ? 2 : toInteger(layout.split("_")[1]);
};

export function setRowLayout(
  page: InsightPage,
  position: number,
  layout: Layout
) {
  return produce(page, (draftPage) => {
    const nb = getLayoutItemNb(layout);
    const oldLayout = get(draftPage, ["rows", position, "layout"]);
    const oldnb = getLayoutItemNb(oldLayout);
    const row = draftPage.rows[position];

    const diff = oldnb - nb;
    if (diff < 0) {
      each(range(diff), () => {
        if (isTitleRow(row)) {
          row.titles.push("");
        } else if (isCardRow(row)) {
          row.cards.push(null);
        }
      });
    } else if (diff > 0) {
      if (isTitleRow(row)) {
        const emptyTitles = times(nb, constant(""));
        const titles = without(row.titles, null, "");
        row.titles = merge(emptyTitles, titles);
        row.titles.splice(nb, diff);
      } else if (isCardRow(row)) {
        const emptyCards = times(nb, constant(null));
        const cards = without(row.cards, null);
        // @ts-ignore
        row.cards = merge(emptyCards, cards);
        row.cards.splice(nb, diff);
      }
    }
    set(draftPage, ["rows", position, "layout"], layout);
  });
}

export function moveRow(page: InsightPage, from: number, to: number) {
  return produce(page, (draftPage) => {
    const f = draftPage.rows.splice(from, 1)[0];
    draftPage.rows.splice(to, 0, f);
  });
}

export function setRowType(
  page: InsightPage,
  position: number,
  style: TitleRow["style"]
) {
  return produce(page, (draftPage) => {
    const row = draftPage.rows[position];
    if (isTitleRow(row)) {
      row.style = style;
    }
  });
}

export function moveCard(
  page: InsightPage,
  row: number,
  from: number,
  to: number
) {
  return produce(page, (draftPage) => {
    const target = draftPage.rows[row];
    if (
      isCardRow(target) &&
      target.cards[from] !== null &&
      to < target.cards.length
    ) {
      const f = target.cards.splice(from, 1)[0];
      target.cards.splice(to, 0, f);
    }

    if (
      (isCardRow(target) || isTitleRow(target)) &&
      rowHasLayoutWithTwoItem(target)
    ) {
      target.layout = getInversedLayout(target.layout);
    }
  });
}

function rowHasLayoutWithTwoItem(row: Row) {
  return (
    !!row &&
    (isCardRow(row) || isTitleRow(row)) &&
    getLayoutItemNb(row.layout) === 2
  );
}

function getInversedLayout(layout: Layout) {
  return layout === Layout.Row_1_2
    ? Layout.Row_2_1
    : layout === Layout.Row_2_1
    ? Layout.Row_1_2
    : Layout.Row_2;
}

export function deleteCard(page: InsightPage, line: number, position: number) {
  return produce(page, (draftPage) => {
    const target = draftPage.rows[line];
    if (isCardRow(target) && target.cards[position]) {
      target.cards[position] = null;
    }
  });
}

export function setCard(
  page: InsightPage,
  line: number,
  position: number,
  card: Card
) {
  return produce(page, (draftPage) => {
    const target = draftPage.rows[line];
    if (isCardRow(target)) {
      target.cards[position] = card;
    }
  });
}

export function setInsightPageTimezone(page: InsightPage, timezone: Timezone) {
  return produce(page, (draft) => {
    const currentTimezone = getTimezoneFromInsightPage(page);
    draft.timezone = timezone;
    each(draft.rows, (row) => {
      if (isCardRow(row)) {
        row.cards = setCardsTimezone(row.cards, currentTimezone, timezone);
      }
    });
  });
}

function getTimezoneFromInsightPage(page: InsightPage) {
  return page.timezone || "UTC";
}

function setCardsTimezone<T extends Card[]>(
  cards: T,
  currentTimezone: Timezone,
  nextTimezone: Timezone
) {
  return map(cards, (card) => {
    return setCardTimezone(card, currentTimezone, nextTimezone);
  }) as T;
}

export function hasCards(page: InsightPage) {
  return some(page.rows, isRowHasCards);
}

function isRowHasCards(row: Row) {
  return isCardRow(row) && some(row.cards, (card) => !!card);
}

function getColorsFromRow(row: Row) {
  if (isTitleRow(row)) {
    return [];
  }
  return isCardRow(row) && map(row.cards, "dataviz.color");
}

export function getColorsFromPage(page: InsightPage): string[] {
  return uniq(compact(flatMap(map(page?.rows, getColorsFromRow))));
}
